/**
 * Делегат автозахвата кликов.
 *
 * PostHog умеет автозахват сам и читает `data-ph-capture-attribute-*` своим
 * SDK. У Метрики такого нет вовсе — есть карта кликов, но это картинка, а не
 * события. Чтобы не потерять уже расставленную разметку (449 атрибутов в 96
 * файлах), читаем те же самые атрибуты сами.
 *
 * Атрибуты остаются единственным источником правды и не переименовываются:
 * префикс `ph-` теперь исторический, зато ни один из 96 файлов трогать не
 * пришлось, и на PostHog разметка продолжает работать штатно.
 *
 * Включается только когда среди активных провайдеров есть такой, у которого
 * своего автозахвата нет (см. needsAutocaptureShim в core.js) — иначе на
 * сборке both каждый клик приехал бы дважды.
 */

const PREFIX = 'phCaptureAttribute'; // data-ph-capture-attribute-foo → dataset.phCaptureAttributeFoo

// dataset даёт camelCase, событию нужен snake_case: Foo → foo, TypeId → type_id.
function toSnake(camel) {
  return camel
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

/**
 * Собирает атрибуты с элемента и всех его предков.
 *
 * Разметка намеренно многоуровневая: `component` висит на контейнере модалки,
 * `action` — на конкретной кнопке внутри. Поэтому одного элемента мало, надо
 * подниматься до самого верха и склеивать. Ближний к клику выигрывает: если
 * вложенный блок переопределил `component`, значит он и есть более точный.
 */
function collectAttributes(start) {
  const props = {};
  let node = start;

  while (node && node !== document.documentElement) {
    const data = node.dataset;
    if (data) {
      for (const key of Object.keys(data)) {
        if (!key.startsWith(PREFIX) || key === PREFIX) continue;
        const name = toSnake(key.slice(PREFIX.length));
        if (!(name in props)) props[name] = data[key];
      }
    }
    node = node.parentElement;
  }

  return props;
}

let attached = false;

export function attachAutocapture(capture) {
  if (attached) return;
  attached = true;

  // Фаза перехвата, а не всплытия: модалки и пикеры у нас регулярно душат
  // всплытие через stopPropagation, и на bubble-слушателе часть кликов просто
  // не доехала бы — причём именно та часть, что внутри модалок, где и
  // происходит основная работа.
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!target || !target.closest) return;

      const props = collectAttributes(target);

      // Клик без разметки не событие. Иначе `ui_click` прилетал бы на каждое
      // касание страницы и утопил бы в шуме те места, которые размечали руками.
      if (Object.keys(props).length === 0) return;

      capture('ui_click', props);
    },
    { capture: true, passive: true },
  );
}
